/**
 * We only import the `AppRouter` type from the server - this is not available at runtime
 */
import type { MonthlyInvoice } from '../../../express-tRPC/src/api/monthly_invoices/types';
import { Component, OnInit } from '@angular/core';
import { environment } from '../environments/environment';
import { NavigationService, ExtendedMenuItem } from './services/navigation/navigation.service';
import { TrpcService } from './services/trpc/trpc.service';
import { AuthService } from './services/auth/auth.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  invoices!: MonthlyInvoice[];
  totalInvoices: any;
  isInvoicelistLoading: boolean = false;
  environment = environment;
  menuItems: ExtendedMenuItem[] = [];
  sidebarVisible: boolean = true;
  menuExpanded: boolean = true;
  loginTime: number = 0;
  initialLogoutTimeDuration: number = 0;
  logoutTimeDuration: number = 0;
  intervalId: any;
  enableSidebar: boolean = false;

  logoutInProcess: boolean = false;

  constructor(
    private navigationService: NavigationService,
    private trpcService: TrpcService,
    private authService: AuthService,
    private router: Router
    ) {
  }

  async ngOnInit() {
    this.authService.isAuthenticatedObservable.subscribe((isAuthenticated: boolean) => {
      this.enableSidebar = isAuthenticated;
    });

    this.trpcService.resetTimerObservable.subscribe((reset: boolean) => {
      if(reset) {
        this.resetLogoutTimer();
      }
    });

    await this.initializeMenu();
  }

  async initializeMenu() {
    try {

      const userInfo = await this.trpcService.trpc.authRouter.getCurrentUser.query();
      const hasPreReleaseAccess = userInfo?.hasPreReleaseAccess || false;

      this.loginTime = userInfo?.lastaccess || 0;
      this.initialLogoutTimeDuration = userInfo?.timeout || 0;
      this.logoutTimeDuration = userInfo?.timeout || 0;

      console.log('User:', userInfo?.username, 'Pre-release access:', hasPreReleaseAccess, 'Last access: ', this.loginTime);
      this.menuItems = this.initializeExpandedMenuItems(hasPreReleaseAccess, userInfo.role);
    } catch (error) {
      console.error('Failed to get user info, showing default menu:', error);
      this.menuItems = this.initializeExpandedMenuItems(false, '');
    }
  }

  /**
   * Initialize menu items with all panels expanded by default
   */
  initializeExpandedMenuItems(hasPreReleaseAccess: boolean = false, userRole: string): ExtendedMenuItem[] {
    console.log("Initializing menu items...");

    let menuItems = this.navigationService.getMenuItems(hasPreReleaseAccess, userRole);

    menuItems = this.setAllItemsExpanded(menuItems);

    // Add the toggle menu item at the bottom
    // This doesn't work even though toggleMenuExpansion() works on a button outside of the menu
    // menuItems.push({
    //   label: 'Toggle Menu Expansion',
    //   icon: 'pi pi-arrows-alt',
    //   command: () => this.toggleMenuExpansion()
    // });
    if(this.logoutTimeDuration) {
      menuItems = [
          {
          label: 'Auto logout in 00:00', // Will be dynamically updated
          icon: 'pi pi-clock',
          id: 'logout-timer',
          disabled: true // so it’s not clickable
        },
        ...menuItems
      ];
      
      this.startLogoutTimer();

    }
    
    return menuItems;
    
  }

  startLogoutTimer() {
    this.updateLogoutLabel();

    this.intervalId = setInterval(() => {
      this.logoutTimeDuration--;

      this.updateLogoutLabel();

      if (this.logoutTimeDuration <= 0) {
        clearInterval(this.intervalId);
        //timer is over handle logout
        this.menuItems = this.menuItems.map(item => item.id === 'logout-timer' ? { ...item, label: "You have been logged out." } : item);
        this.authService.clearCookieFromStorage();
        this.trpcService.setToken({ cookie: { name: '', value: '' } });
      }
    }, 1000);
  }

  updateLogoutLabel() {
    const hours = Math.floor(this.logoutTimeDuration / 3600);
    const minutes = Math.floor((this.logoutTimeDuration % 3600) / 60);
    const seconds = this.logoutTimeDuration % 60;

    const formatted =
      hours > 0
        ? `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        : `${minutes}:${seconds.toString().padStart(2, '0')}`;

    this.menuItems = this.menuItems.map(item => item.id === 'logout-timer' ? { ...item, label: `Auto logout in ${formatted}` } : item);

  }

  resetLogoutTimer() {
    this.logoutTimeDuration = this.initialLogoutTimeDuration;
  }

  /**
   * Recursively sets the expanded property for all menu items
   */
  setAllItemsExpanded(items: ExtendedMenuItem[]): ExtendedMenuItem[] {

    if (items.length === 0) {
      // console.log(`Skipping setAllItemsExpanded() because menuItems is empty`);
      return items;
    }

    return this.toggleAllRecursive(items, true);

  }

  private toggleAllRecursive(items: ExtendedMenuItem[], expanded: boolean): ExtendedMenuItem[] {
    return items.map((menuItem) => {
      menuItem.expanded = expanded;
      if (menuItem.items) {
        menuItem.items = this.toggleAllRecursive(menuItem.items, expanded);
      }
      return menuItem;
    });
  }

  /**
   * Toggle expansion state of all menu items to the opposite of the current state
   */
  toggleMenuExpansion(): void {

    // Reverse the current state of the menu expansion
    if (this.menuExpanded) {
      this.menuExpanded = false;
    } else {
      this.menuExpanded = true;
    }

    // The act of assigning the updated menu model will refresh the vide.
    this.menuItems = this.toggleAllRecursive(this.menuItems, this.menuExpanded);
  }

  toggleSidebar() {
    this.sidebarVisible = !this.sidebarVisible;
  }

  async logout() {
    this.logoutInProcess = true;

    const result: any = await this.trpcService.trpc.authRouter.logoutNG.mutate();
    
    this.logoutInProcess = false;

    // Clear the authentication cookie from localStorage
    this.authService.clearCookieFromStorage();
    console.log('Cookie cleared from localStorage');

    // Reset TrpcService token to empty state
    this.trpcService.setToken({ cookie: { name: '', value: '' } });

    this.authService.isAuthenticatedObservable.next(false);

    this.router.navigate(['/login']);
  }
}
